// In dev: Vite proxy routes /api/* → localhost:3002 (see vite.config.ts).
// In production: Express serves both the React build and /api/* from the same origin.
// Default to '/api' so all query paths resolve correctly in both environments.
export const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api'

export class ApiError extends Error {
  /**
   * The response body exactly as the server sent it.
   *
   * KAN-820: `message` used to be the raw body, so callers that wanted the
   * structured envelope read it off `message`. It now carries the extracted
   * human-readable reason instead, and the envelope lives here — read `body`
   * when you need to parse fields such as an error code or warnings, and
   * `message` when you need a string to show a person.
   */
  readonly body: string

  constructor(
    public status: number,
    message: string,
    body?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.body = body ?? message
  }
}

/**
 * Pull the human-readable reason out of an error response body.
 *
 * KAN-820: the raw body went straight into `ApiError.message`, so whatever the
 * UI rendered was whatever the server happened to send — for an unhandled API
 * error that was an entire HTML document, which is why the admin AI-tasks page
 * could show nothing but "Something went wrong". API errors are JSON
 * (`{ error, message? }`), so prefer `message`, then a string `error`, and
 * fall back to the raw body for anything else.
 */
export function errorMessageFromBody(body: string): string {
  const trimmed = body.trim()
  if (!trimmed.startsWith('{')) return body
  try {
    const parsed = JSON.parse(trimmed) as { message?: unknown; error?: unknown }
    if (typeof parsed.message === 'string' && parsed.message) return parsed.message
    if (typeof parsed.error === 'string' && parsed.error) return parsed.error
  } catch {
    // Not JSON after all — fall through to the raw body.
  }
  return body
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${path}`
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  })

  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new ApiError(
      response.status,
      errorMessageFromBody(body) || response.statusText,
      body,
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json() as Promise<T>
}

export const api = {
  get<T>(path: string): Promise<T> {
    return request<T>(path)
  },
  post<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: 'POST', body: JSON.stringify(body) })
  },
  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: 'PUT', body: JSON.stringify(body) })
  },
  patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
  },
  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'DELETE' })
  },
}

// ─── Query string helpers ─────────────────────────────────────────────────────

export function buildQueryString(
  params: Record<string, string | number | boolean | string[] | undefined | null>,
): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      value.forEach((v) => qs.append(key, String(v)))
    } else {
      qs.set(key, String(value))
    }
  }
  const str = qs.toString()
  return str ? `?${str}` : ''
}
